import { Component } from 'react'
import { CtaInsightOptions, Insight, InsightCTAFullContainer, InsightCTAKey, LinkCTA, TrackerCTA } from 'interfaces/insights'
import { PageType } from 'interfaces/pages'
import { OPPORTUNITIES_PAGE, FEED_POST_PAGE, OFFER_PAGE, FEED_AUTHOR_PAGE } from 'constants/pages'
import { Timeframe } from 'interfaces/chart'
import { CategoryId } from 'interfaces/meta'
import { getCTACategory, getCTATimeframe, isModuleInsight, getInsightLinkTo, EDIT_CTA_TARGET_MAP } from 'helpers/insightHelpers'
import { getAnchorPage, isCategorizedPage, isTimeframedPage } from 'helpers/pageHelpers'
import { generate } from 'randomstring'
import * as styles from 'components/features/insights/InsightFooter/InsightFooter.css'
import * as cn from 'classnames'
import InsightButton, { InsightButtonProps, NoLink, Link } from 'components/features/insights/InsightButton/InsightButton'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { ShadowKind } from 'interfaces/component'
import { ButtonSize } from 'components/features/pfm/Button/Button'
import { SocialInnerInsight } from 'interfaces/socialInsights'

type CurrentPageParams = {
  page: PageType
  timeframe: Timeframe | null
  categoryId: CategoryId | null
}

export type InsightFooterOwnProps = {
  insight: Insight | SocialInnerInsight
  hasNext?: boolean
  buttonSize?: ButtonSize
  fullWidth?: boolean
  narrowDismiss?: boolean
}

export type InsightFooterDispatchProps = {
  seeNext: () => void
  dismiss: () => void
  undismiss: () => void
  closeNotifications: () => void
  performCta: <T extends InsightCTAKey>(
    type: T,
    cta: InsightCTAFullContainer[T] | undefined,
    insight: Insight | SocialInnerInsight,
    options?: CtaInsightOptions,
  ) => void
}

export type InsightFooterStateProps = CurrentPageParams

const isCTACausingInsightsReset = (cta: LinkCTA | TrackerCTA, { page, timeframe, categoryId }: CurrentPageParams): boolean => {
  const ctaPage = getAnchorPage(cta.target)
  if (
    ctaPage !== page
    // opportunities are in state already
    && ctaPage !== OPPORTUNITIES_PAGE
  ) {
    return true
  }

  const ctaTimeframe = isTimeframedPage(ctaPage) && cta.filters ? getCTATimeframe(cta.filters) : null
  if (timeframe && ctaTimeframe && timeframe !== ctaTimeframe) {
    return true
  }

  const ctaCategoryId = isCategorizedPage(ctaPage) ? getCTACategory(cta.filters) : null
  if (categoryId && ctaCategoryId && categoryId !== ctaCategoryId) {
    return true
  }

  return false
}

type InsightFooterProps = InsightFooterOwnProps & InsightFooterStateProps & InsightFooterDispatchProps

class InsightFooter extends Component<InsightFooterProps> {
  render () {
    const leftButtonDescriptors = this.getLeftButtonDescriptors()
    const rightButtonDescriptors = this.getRightButtonDescriptors()
    const isLeftSideInverted = isModuleInsight(this.props.insight) && rightButtonDescriptors.length >= 2

    const children = (
      <>
        { this.renderButtonSide('left', leftButtonDescriptors, isLeftSideInverted) }
        { this.renderButtonSide('right', rightButtonDescriptors) }
      </>
    )

    if (!this.props.fullWidth) {
      return children
    }

    return (
      <div className={ styles.fullWidth }>
        { children }
      </div>
    )
  }

  renderButtonSide (side: 'left' | 'right', descriptors: InsightButtonProps[], isInverted?: boolean) {
    const { fullWidth, narrowDismiss } = this.props
    if (!descriptors.length) {
      return null
    }

    const className = cn(styles.buttonSide, styles[side], {
      [styles.inverted]: isInverted,
      [styles.fullWidth]: fullWidth,
      [styles.minWidth]: side === 'left' && narrowDismiss,
    })

    return (
      <div className={ className }>
        { descriptors.map((descriptor, index) => {
          const withShadow = false
            && side === 'right'
            && index === descriptors.length - 1
            && descriptor.theme !== 'text-only'

          return this.renderButton(descriptor, String(index), withShadow ? 'light' : undefined)
        }) }
      </div>
    )
  }

  renderButton = (descriptor: InsightButtonProps, key: string, shadow?: ShadowKind) => {
    const { fullWidth, buttonSize } = this.props
    return (
      <InsightButton
        { ...descriptor }
        shadow={ shadow }
        key={ key }
        size={ buttonSize }
        fullWidth={ fullWidth }
      />
    )
  }

  private getLeftButtonDescriptors () {
    const descriptors: InsightButtonProps[] = []
    const { insight } = this.props
    const { cta, is_dismissed } = insight

    if (!cta) {
      return descriptors
    }

    if (cta.dismiss) {
      const { dismiss, undismiss } = this.props
      descriptors.push({
        linkComponent: NoLink,
        theme: cta.dismiss.theme || 'blue',
        children: is_dismissed ? 'Undismiss' : 'Dismiss',
        onClick: is_dismissed ? undismiss : dismiss,
      })
    }

    return descriptors
  }

  private getRightButtonDescriptors () {
    const descriptors: InsightButtonProps[] = []
    const { insight, hasNext, performCta, dismiss, closeNotifications } = this.props
    const { cta } = insight

    if (!cta) {
      return descriptors
    }

    if (hasNext) {
      const { seeNext } = this.props
      descriptors.push({
        linkComponent: NoLink,
        theme: 'text-only',
        children: 'See Next',
        onClick: seeNext,
      })
    }

    if (cta.click) {
      descriptors.push({
        linkComponent: NoLink,
        theme: 'outlined',
        children: 'Learn More',
        onClick: cta.click.onClick,
      })
    }

    const { to } = getInsightLinkTo(cta)

    if (cta.popup) {
      descriptors.push({
        linkComponent: NoLink,
        theme: cta.popup.theme,
        children: cta.popup.title || 'Learn More',
        onClick: () => performCta('popup', cta.popup, insight),
      })
    }

    if (cta.link) {
      const { page, timeframe, categoryId } = this.props
      const isCausingInsightsReset = isCTACausingInsightsReset(cta.link, { page, timeframe, categoryId })
      descriptors.push({
        linkComponent: NavigationLink,
        to: to || cta.link.target,
        theme: cta.link.theme,
        children: cta.link.title || 'Learn More',
        onClick: () => {
          performCta('link', cta.link, insight, { isCausingInsightsReset })
          closeNotifications()
        },
      })
    }

    if (cta.link_creditfile) {
      descriptors.push({
        linkComponent: NavigationLink,
        to: to || 'credit-linkage',
        theme: cta.link_creditfile.theme,
        children: cta.link_creditfile.title || 'Link Credit File',
        onClick: () => performCta('link_creditfile', cta.link_creditfile, insight),
      })
    }

    if (cta.set_tracker) {
      const { page, timeframe, categoryId } = this.props
      const isCausingInsightsReset = isCTACausingInsightsReset(cta.set_tracker, { page, timeframe, categoryId })
      descriptors.push({
        linkComponent: NavigationLink,
        to: to || cta.set_tracker.target,
        theme: cta.set_tracker.theme,
        children: cta.set_tracker.title || 'Set Tracker',
        onClick: () => performCta('set_tracker', cta.set_tracker, insight, { isCausingInsightsReset, eventId: generate() }),
      })
    }

    if (cta.edit_tracker) {
      const { page, timeframe, categoryId } = this.props
      const isCausingInsightsReset = isCTACausingInsightsReset(cta.edit_tracker, { page, timeframe, categoryId })
      descriptors.push({
        linkComponent: NavigationLink,
        to: to || cta.edit_tracker.target,
        theme: cta.edit_tracker.theme,
        children: cta.edit_tracker.title || 'Set Tracker',
        onClick: () => performCta('edit_tracker', cta.edit_tracker, insight, { isCausingInsightsReset, eventId: generate() }),
      })
    }

    if (cta.edit) {
      descriptors.push({
        linkComponent: NavigationLink,
        to: to || EDIT_CTA_TARGET_MAP[cta.edit.field],
        theme: cta.edit.theme,
        children: cta.edit.title || 'Edit',
        onClick: () => performCta('edit', cta.edit, insight),
      })
    }

    if (cta.add_account) {
      descriptors.push({
        linkComponent: NoLink,
        theme: cta.add_account.theme,
        children: cta.add_account.title || 'Link Account',
        onClick: () => performCta('add_account', cta.add_account, insight),
      })
    }

    if (cta.add_car) {
      descriptors.push({
        linkComponent: NoLink,
        theme: cta.add_car.theme,
        children: cta.add_car.title || 'Add Car',
        onClick: () => performCta('add_car', cta.add_car, insight),
      })
    }

    if (cta.add_home) {
      descriptors.push({
        linkComponent: NoLink,
        theme: cta.add_home.theme,
        children: cta.add_home.title || 'Add Home',
        onClick: () => performCta('add_home', cta.add_home, insight),
      })
    }

    if (cta.update_credentials) {
      descriptors.push({
        linkComponent: NavigationLink,
        to: to || 'linked-accounts',
        theme: cta.update_credentials.theme,
        children: cta.update_credentials.title || 'Update',
        onClick: () => performCta('update_credentials', cta.update_credentials, insight),
      })
    }

    if (cta.view_linked_offer) {
      descriptors.push({
        linkComponent: NavigationLink,
        to: to || { anchor: OFFER_PAGE, id: cta.view_linked_offer.offer_id },
        theme: cta.view_linked_offer.theme,
        children: cta.view_linked_offer.title,
        onClick: () => performCta('view_linked_offer', cta.view_linked_offer, insight),
      })
    }

    if (cta.offer) {
      descriptors.push({
        linkComponent: Link,
        to: to || cta.offer.url,
        theme: cta.offer.theme,
        target: '_blank',
        children: cta.offer.title || 'See Offer',
        onClick: () => performCta('offer', cta.offer, insight),
      })
    }

    if (cta.view_post) {
      descriptors.push({
        linkComponent: NavigationLink,
        to: to || { anchor: FEED_POST_PAGE, id: cta.view_post.post_uid },
        theme: cta.view_post.theme || 'text-only',
        children: 'View Post',
        onClick: () => performCta('view_post', cta.view_post, insight),
      })
    }

    if (cta.view_comment) {
      descriptors.push({
        linkComponent: NavigationLink,
        to: to || { anchor: FEED_POST_PAGE, id: cta.view_comment.post_uid },
        theme: cta.view_comment.theme || 'text-only',
        children: 'View Comment',
        onClick: () => performCta('view_comment', cta.view_comment, insight),
      })
    }

    if (cta.view_social_profile) {
      descriptors.push({
        linkComponent: NavigationLink,
        to: to || { anchor: FEED_AUTHOR_PAGE, id: cta.view_social_profile.social_user_uid },
        theme: cta.view_social_profile.theme || 'text-only',
        children: 'View Social Profile',
        onClick: () => performCta('view_social_profile', cta.view_social_profile, insight),
      })
    }

    if (cta.allow_push_notifications) {
      const { theme, title } = cta.allow_push_notifications

      descriptors.push({
        linkComponent: NoLink,
        theme: theme || 'blue',
        children: title || 'Activate',
        onClick: () => {
          performCta('allow_push_notifications', cta.allow_push_notifications, insight)
          dismiss()
        },
      })
    }

    return descriptors
  }
}

export default InsightFooter
